<template>
    <div v-if="showPublish" class="publish-container">
        <div class="publish-title">
            <DsIconButton @click="closePublish">
                <ds-icon name="x" />
            </DsIconButton>

            <div class="publish-title-text">
                {{ $t('funnel.analysis.checklist') }}
            </div>
        </div>

        <div v-if="loaded">
            <div
                class="publish-analysis-info"
                @mouseleave="hideDescription"
            >
                <DsListItem
                    v-for="(item, i) in analyzeFunnelData"
                    :key="i"
                    :title="item.checkListDisplayText"
                    class="list-analysis-info-item cursor-pointer"
                    @click="showDetails(item)"
                    @mouseover="showDescription(item.checklistItemDescription, i)"
                >
                    <template #leading>
                        <DsIcon :name="item.nameIcon" :class="item.classIcon" />
                    </template>

                    <template #trailing>
                        <DsIcon v-if="item.metaData" name="chevron-right" />
                    </template>
                </DsListItem>

                <div v-if="showDesc" class="publish-item-description">
                    <DsIcon name="info-circle" />
                    <span> {{ description }} </span>
                </div>
            </div>

            <div class="button-row-publish">
                <DsTextButton
                    class="cancel-button"
                    @click="closePublish"
                >
                    {{ $t('funnel.edit.cancel') }}
                </DsTextButton>

                <DsFilledButton
                    class="publish-button"
                    :disabled="disablePublish"
                    @click="publish"
                >
                    {{ $t('funnel.edit.publish') }}
                </DsFilledButton>
            </div>
        </div>

        <div v-else class="publish-spinner">
            <DsSpinner :size="100" />
        </div>
    </div>
    <div v-else class="publish-container">
        <div class="publish-title">
            <DsIconButton @click="hideDetails">
                <DsIcon name="arrow-left" />
            </DsIconButton>

            <div class="publish-title-text">
                {{ detailTitle }}
            </div>
        </div>
        <div v-if="loaded" class="publish-analysis-info">
            <DsListItem
                v-for="(item, i) in details"
                :key="i"
                :title="item.title"
                class="cursor-pointer"
                @click="openCellModalById(item)"
            >
                <template #leading>
                    <DsIcon
                        :name="detailIcon"
                        :class="detailClassIcon"
                    />
                </template>

                <template #description>
                    <DsListItem
                        v-for="(value, j) in item.list"
                        :key="j"
                        :title="value"
                        class="description-item-list"
                    />
                </template>

                <template #trailing>
                    <DsIcon name="eye-show" />
                </template>
            </DsListItem>
        </div>
    </div>
</template>

<script>
import { analyzeFunnelBeforePublishing } from './publish.api';
import axios from 'axios';
import { POST_20240130_SENDABLE_TOLL_FREE_SMS_STATUSES } from '../../utils/smsBroadcast.constants';

export default {
    props: {
        funnelId: {
            type: Number,
            required: true,
        },
        funnelDraftXml: {
            type: String,
            default: '',
        },
    },

    emits: ['close-publish', 'verify-and-publish', 'open-cell-modal-by-id'],

    data() {
        return {
            loaded: false,
            analyzeFunnelData: {},
            data: {},
            funnelLocked: false,
            showPublish: true,
            detailTitle: '',
            details: {},
            detailIcon: '',
            detailClassIcon: '',
            showDesc: false,
            description: '',
            addContactsWhenPublishing: '',
            disablePublish: false,
            enablePublishButtonIfVerificationStatusIsApproved: false,
        };
    },

    watch: {
        funnelDraftXml(value) {
            if (value) {
                this.loaded = false;
                this.loadPublishInfo();
                this.hideDetails();
            }
        },
    },

    async mounted() {
        if (this.shouldCheckTextMessageNode()) {
            await this.loadAutomatedSmsAccount();
        }
        await this.loadPublishInfo();
    },

    methods: {
        async loadPublishInfo() {
            try {
                this.data = await analyzeFunnelBeforePublishing(this.funnelId, this.funnelDraftXml);
                this.analyzeFunnelData = this.data?.results?.filter((item) => item.type !== 'ADD_CONTACTS_BY_TAG');
                this.addContactsWhenPublishing = this.data?.results?.filter((item) => item.type === 'ADD_CONTACTS_BY_TAG')[0]?.count > 0;
                this.funnelLocked = this.data?.funnelLocked;
                const hasErrorResults = this.data?.results?.some((item) => item.status === 'ERROR');
                const isVerificationStatusApproved = this.enablePublishButtonIfVerificationStatusIsApproved;
                const isTextMessageNodeEnabled = this.shouldCheckTextMessageNode();

                this.disablePublish = isTextMessageNodeEnabled
                    ? hasErrorResults || isVerificationStatusApproved
                    : hasErrorResults;

                this.loaded = true;
            } catch {
                this.loaded = false;
            }
        },

        shouldCheckTextMessageNode() {
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(this.funnelDraftXml, 'application/xml');

            // Search for the node with the specific name attribute
            const sendTextMessageNode = xmlDoc.querySelector('mxCell > Object[name="Send text message"]');

            return !!sendTextMessageNode;
        },

        async loadAutomatedSmsAccount() {
            try {
                const { data } = await axios.get('/app/automatedSmsFunnel/getAccount');

                if (data) {
                    this.enablePublishButtonIfVerificationStatusIsApproved = data?.verificationStatus && !POST_20240130_SENDABLE_TOLL_FREE_SMS_STATUSES.includes(data?.verificationStatus);
                }
            } catch (e) {
                this.enablePublishButtonIfVerificationStatusIsApproved = false;
            }
        },

        showDetails({ nameIcon, classIcon, metaData }) {
            if (!metaData) return;

            this.showPublish = false;
            this.detailTitle = metaData.detailTitle;
            this.detailIcon = nameIcon;
            this.detailClassIcon = classIcon;

            this.details = [];

            metaData.details.forEach((item) => {
                if (item.detailList.length > 0) {
                    item.detailList.forEach((subItem) => {
                        const detail = {};

                        detail.showMeId = item.showMeId;
                        detail.title = subItem.subDetailItem;
                        detail.list = subItem.subDetailList;
                        this.details.push(detail);
                    });
                } else {
                    const detail = {};

                    detail.showMeId = item.showMeId;
                    detail.title = item.detailItem;
                    detail.list = [];
                    this.details.push(detail);
                }
            });
        },

        hideDetails() {
            this.showPublish = true;
        },

        showDescription(description) {
            this.description = description;
            this.showDesc = true;
        },

        hideDescription() {
            this.showDesc = false;
            this.description = '';
        },

        closePublish() {
            this.$emit('close-publish');
        },

        publish() {
            this.loaded = false;

            const event = {
                addContacts: this.addContactsWhenPublishing,
            };

            this.$emit('verify-and-publish', event);
        },

        isClickable({ showMeId }) {
            return showMeId && showMeId !== '#';
        },

        openCellModalById(item) {
            if (!this.isClickable(item)) return;

            this.$emit('open-cell-modal-by-id', { cellId: item.showMeId });
        },
    },
};
</script>

<style lang="scss" scoped>
$publish-header-footer-height: px-to-rem(150);

.publish-analysis-info {
    overflow-y: auto;
    max-height: calc(100vh - $publish-header-footer-height);
}

.publish-container {
    height: 100%;
    width: px-to-rem(450);
    z-index: 1000;
    background-color: $color-paper;
    transition: right .5s;
    position: fixed;
    right: 0;
    top: 0;
    display: flex;
    flex-direction: column;
    box-shadow: 0 0  px-to-rem(10) rgba(0,0,0,0.2);
}

.description-item-list:last-of-type:deep(.list-item-info) {
    border-bottom: 0;
}

.publish-title {
    padding: 0.90rem;
    border-bottom: px-to-rem(0.5) solid $color-gray-200;
    display: inline-flex;

    .publish-title-text {
        font-size: $font-size-lg;
        line-height: $line-height-md;
        margin: auto 0;
    }
}

div.button-row-publish {
    padding: $spacing-250 $spacing-200;
    box-shadow: rgba(0, 0, 0, 0.2) 0 px-to-rem(-6) px-to-rem(6) px-to-rem(-6);
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    background-color: $color-paper;
}

.publish-check-icon {
    fill: $color-green-400;
}

.publish-alert-icon {
    fill: $color-orange-500;
}

.publish-error-icon {
    fill: $color-red-500;
}

.publish-item-description {
    background-color: $color-gray-100;
    margin: $spacing-250;
    padding: $spacing-125;
    border-radius: px-to-rem(10);
    display: flex;

    svg {
        color: $color-ink-600;
        margin-right: $spacing-125;
    }
}

.list-item:deep(.list-item-info) {
    padding-bottom: 0;
    padding-right: 0;

    .trailing-slot {
        padding: $spacing-100;
    }

    &:hover .trailing-slot {
        --icon-color: rgba(0,0,0,1);
    }
    .item-text .title.one-line {
        display: -webkit-box;
        text-wrap: wrap;
        text-overflow: ellipsis;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        white-space: normal; /* Ensure this is set to normal to allow wrapping */
        padding: 0;

        &:hover {
            line-clamp: unset;
            -webkit-line-clamp: unset;
        }
    }

    .title:not(:only-child) {
        margin: $spacing-100 0 0;
    }
}
.publish-spinner {
    margin: auto;
}

.cursor-pointer {
    cursor: pointer;
}
</style>
